import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { useIsMobile } from "@/hooks/useIsMobile";

export default function CTAFinal({ onDemoClick }) {
  const ref = useRef(null);
  const isMobile = useIsMobile();
  const prefersReducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y1 = useTransform(scrollYProgress, [0, 1], isMobile ? [0, 0] : [80, -80]);
  const y2 = useTransform(scrollYProgress, [0, 1], isMobile ? [0, 0] : [-30, 30]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], isMobile ? [1, 1, 1] : [0.9, 1, 1.05]);

  return (
    <section
      ref={ref}
      data-testid="cta-final"
      className="relative py-16 md:py-28 bg-[#F2EDE3] overflow-hidden"
    >
      <motion.div
        style={{ y: prefersReducedMotion ? 0 : y1 }}
        className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#a68a6d]/8 blur-3xl pointer-events-none"
        aria-hidden="true"
      />
      <motion.div
        style={{ y: prefersReducedMotion ? 0 : y2 }}
        className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#111111]/10 blur-3xl pointer-events-none"
        aria-hidden="true"
      />

      <motion.div
        style={{ scale: prefersReducedMotion ? 1 : scale }}
        className="relative max-w-5xl mx-auto px-5 sm:px-8 text-center"
      >
        <motion.h2
          initial={{ opacity: 0, y: isMobile ? 20 : 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: isMobile ? 0.45 : 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="font-['Cabinet_Grotesk'] font-black tracking-tighter leading-[0.9] text-[#075a22] text-4xl sm:text-5xl md:text-8xl"
        >
          Deja de perseguir.
          <br />
          <span className="text-[#a68a6d]">Empieza a cobrar.</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: isMobile ? 14 : 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: isMobile ? 0.4 : 0.7, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 md:mt-8 text-lg md:text-2xl text-[#111111]/85 max-w-2xl mx-auto"
        >
          Únete a los negocios que están cobrando sus membresías en piloto
          automático. Sin mensualidad. Solo 2% cuando cobras.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: isMobile ? 14 : 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: isMobile ? 0.4 : 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 md:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <button
            onClick={onDemoClick}
            data-testid="cta-final-primary"
            className="group inline-flex items-center gap-2 bg-[#a68a6d] hover:bg-[#8f7659] text-white text-base md:text-lg font-bold px-6 md:px-8 py-4 md:py-5 rounded-full transition-all hover:scale-[1.03] shadow-[0_16px_40px_rgba(166,138,109,0.35)]"
          >
            Agendar demo
            <ArrowUpRight
              className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              strokeWidth={2.5}
            />
          </button>
          <a
            href="http://app.kubopayments.com/register"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-final-register"
            className="group inline-flex items-center gap-2 bg-[#111111] hover:bg-[#2a2a2a] text-white text-base md:text-lg font-bold px-6 md:px-8 py-4 md:py-5 rounded-full transition-all hover:scale-[1.03] shadow-[0_16px_40px_rgba(17,17,17,0.3)]"
          >
            Registro gratuito
            <ArrowUpRight
              className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              strokeWidth={2.5}
            />
          </a>
        </motion.div>

        <p className="mt-5 md:mt-6 text-sm md:text-base text-[#111111]/65">
          Sin tarjeta de crédito para empezar. Cancela cuando quieras.
        </p>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: isMobile ? 0.35 : 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 md:mt-8"
        >
          <a
            href="https://wa.me/18299278886"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-final-whatsapp"
            className="inline-flex items-center gap-2 text-[#a68a6d] hover:text-[#8f7659] font-medium transition-colors text-sm md:text-base"
          >
            <MessageCircle className="w-5 h-5" strokeWidth={2} />
            ¿Prefieres escribirnos? WhatsApp: +1 (829) 927‑8886
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
}
